import React from 'react';
import { Card } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { CheckCircle, AlertTriangle, Send, Save, Eye, Users, Mail, Target, Calendar } from 'lucide-react';

interface CampaignPreviewProps {
  data: any;
  onSave: (isDraft?: boolean) => void;
  isSaving: boolean;
}

export const CampaignPreview: React.FC<CampaignPreviewProps> = ({ data, onSave, isSaving }) => {
  const completionItems = [
    { label: 'Campaign Details', completed: Boolean(data.name && data.objective), required: true },
    { label: 'Campaign Type', completed: Boolean(data.type), required: true },
    { label: 'Target Criteria', completed: Boolean(data.targetCriteria), required: false },
    { label: 'Email Templates', completed: Boolean(data.templates?.length), required: true },
    { label: 'Sequence Setup', completed: Boolean(data.sequences?.length), required: true },
    { label: 'Settings Configuration', completed: Boolean(data.settings), required: false },
  ];

  const completionRate = Math.round((completionItems.filter(item => item.completed).length / completionItems.length) * 100);
  const requiredComplete = completionItems.filter(item => item.required).every(item => item.completed);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
          Campaign Preview
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Review your campaign settings before launching
        </p>
      </div>

      {/* Completion Status */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Setup Progress
          </h3>
          <div className="flex items-center space-x-2">
            <div className="w-16 h-16 relative">
              <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-neutral-200 dark:text-neutral-700"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray={`${completionRate}, 100`}
                  className="text-blue-500"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {completionRate}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {completionItems.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className="flex items-center space-x-3">
                {item.completed ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-neutral-300 dark:border-neutral-600" />
                )}
                <span className={`font-medium ${item.completed ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-600 dark:text-neutral-400'}`}>
                  {item.label}
                </span>
                {item.required && (
                  <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-1 rounded-full">
                    Required
                  </span>
                )}
              </div>
              <span className={`text-sm ${item.completed ? 'text-green-600 dark:text-green-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
                {item.completed ? 'Complete' : 'Pending'}
              </span>
            </div>
          ))}
        </div>

        {!requiredComplete && (
          <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm text-orange-700 dark:text-orange-300">
                Please complete all required sections before launching
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* Campaign Summary */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Campaign Summary
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Target className="w-5 h-5 text-blue-500" />
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">{data.name || 'Untitled Campaign'}</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{data.objective || 'No objective set'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-green-500" />
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Campaign Type</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 capitalize">{data.type || 'Not selected'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-purple-500" />
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Priority</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 capitalize">{data.priority || 'Medium'}</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-orange-500" />
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Timeline</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {data.startDate && data.endDate 
                    ? `${data.startDate} to ${data.endDate}`
                    : 'No timeline set'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">$</span>
              </div>
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Budget</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {data.budget > 0 ? `${data.currency} ${data.budget.toLocaleString()}` : 'No budget set'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">#</span>
              </div>
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Tags</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {data.tags?.length > 0 ? data.tags.join(', ') : 'No tags set'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => onSave(true)} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            Save as Draft
          </Button>
          <Button variant="outline">
            <Eye className="w-4 h-4 mr-2" />
            Preview Email
          </Button>
        </div>
        <Button 
          variant="primary"
          onClick={() => onSave(false)} 
          disabled={isSaving || !requiredComplete}
          className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          {requiredComplete ? 'Launch Campaign' : 'Complete Required Fields'}
        </Button>
      </div>
    </div>
  );
}; 